import { useLazyQuery } from '@apollo/client'
import { FormControl, InputLabel } from '@mui/material'
import Container from '@mui/material/Container'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { DatePicker } from '@mui/x-date-pickers'
import IntakeCard from 'candidate/components/IntakeCard'
import { useEffect, useState } from 'react'
import 'react-phone-number-input/style.css'
import { GetCompanyOffices } from 'recruiter/graphql/companies.gql'
import NextButton from 'shared/components/NextButton'
import SecondaryButton from 'shared/components/SecondaryButton'
import TertiaryButton from 'shared/components/TertiaryButton'
import { getPxAsRem } from 'shared/utils'
import {
    isEndDateAfterStartForMMyyyy,
    MMyyyyToValidDateString,
} from 'shared/utils/dateFormatter'
import { AdditionalButtonsContainer } from '../styles'
import { FieldWrapper } from './styles'

import type { ChoiceBucket } from 'candidate/models/intake'
import { formatDate } from 'date-fns'
import type { Dispatch, FC, SetStateAction } from 'react'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'

export interface PriorEmploymentAnswer {
    id: number
    company: (ChoiceBucket & { id: string }) | undefined
    companyOffice: ChoiceBucket | undefined
    workTitle: string
    startDate: string
    endDate: string
    industry: ChoiceBucket | undefined
    sector: ChoiceBucket | undefined
    error?: string
}

interface Props {
    answer?: PriorEmploymentAnswer[]
    callback: Dispatch<any>
    workTitles: string[]
    industryChoices: ChoiceBucket[]
    sectorChoices: ChoiceBucket[]
    prompt: string
    skipCallback?: Dispatch<SetStateAction<string>>
    skipText?: string
}

const PriorEmploymentQuestion: FC<Props> = ({
    answer,
    callback,
    workTitles,
    industryChoices,
    sectorChoices,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const [entries, setEntries] = useState<PriorEmploymentAnswer[]>([
        {
            id: 0,
            company: undefined,
            companyOffice: undefined,
            workTitle: '',
            startDate: '',
            endDate: '',
            industry: undefined,
            sector: undefined,
            error: '',
        },
    ])

    const [companyOffices, setCompanyOffices] = useState<
        Record<string, ChoiceBucket[]>
    >({})

    // Set up an on-demand query for company office locations.
    const [getCompanyOffices, { loading: isOfficeLocationDataLoading }] =
        useLazyQuery(GetCompanyOffices, {
            onCompleted: (data) => {
                if (data.company.value in companyOffices) return

                const companyOfficeChoices =
                    data.company.companyOffices?.map(({ officeLocation }) => ({
                        choice: officeLocation?.value ?? '',
                    })) ?? []

                setCompanyOffices({
                    ...companyOffices,
                    [data.company.value]: companyOfficeChoices,
                })
            },
        })

    // Pre-fill the form with any existing answers, fetching dependent values
    // as needed.
    useEffect(() => {
        if (answer && answer.length > 0) {
            const entries = answer.map((entry, index) => ({
                id: index,
                company: entry.company,
                companyOffice: entry.companyOffice,
                workTitle: entry.workTitle,
                startDate: entry.startDate,
                endDate: entry.endDate,
                industry: entry.industry,
                sector: entry.sector,
                error: '',
            }))

            entries.forEach((entry) => {
                if (!entry.company) return

                getCompanyOffices({
                    variables: { companyId: entry.company.id },
                })
            })

            setEntries(entries)
        }
    }, [answer, getCompanyOffices])

    // Add a new entry
    const addEntry = () => {
        setEntries([
            ...entries,
            {
                id: entries.length + 1,
                company: undefined,
                companyOffice: undefined,
                workTitle: '',
                startDate: '',
                endDate: '',
                industry: undefined,
                sector: undefined,
                error: '',
            },
        ])
    }

    // Remove the last entry
    const removeEntry = () => {
        if (entries.length > 1) {
            setEntries(entries.slice(0, -1))
        }
    }

    const entryIsValid = (entry: PriorEmploymentAnswer) => {
        return (
            entry.error === '' &&
            entry.company !== undefined &&
            entry.companyOffice !== undefined &&
            entry.workTitle !== '' &&
            entry.startDate !== '' &&
            entry.endDate !== '' &&
            entry.industry !== undefined &&
            entry.sector !== undefined &&
            isEndDateAfterStartForMMyyyy(entry.startDate, entry.endDate)
        )
    }

    const updateEntry = (
        id: number,
        updatedEntry: Partial<PriorEmploymentAnswer>
    ) => {
        setEntries(
            entries.map((entry) => {
                if (entry.id === id) {
                    const updated = { ...entry, ...updatedEntry }
                    return updated
                }
                return entry
            })
        )
    }

    useEffect(() => {
        if (answer) {
            setEntries(answer)
        }
    }, [answer])

    const submitData = () => {
        const response = entries
        callback({ response })
    }

    return (
        <IntakeCard
            question={prompt}
            skipCallback={skipCallback}
            skipText={skipText}
        >
            {entries.map((entry, index) => (
                <Container
                    key={entry.id}
                    style={{
                        display: 'inline-block',
                        alignContent: 'left',
                        marginBottom: getPxAsRem(36),
                        marginTop: getPxAsRem(16),
                        padding: '0',
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    <CompaniesAutocomplete
                        sx={{ marginBottom: getPxAsRem(24) }}
                        value={
                            entry.company
                                ? {
                                      id: entry.company.id,
                                      value: entry.company.choice,
                                  }
                                : null
                        }
                        onChange={(e, nextValue) => {
                            if (nextValue?.id && nextValue?.value) {
                                updateEntry(entry.id, {
                                    company: {
                                        id: nextValue.id,
                                        choice: nextValue.value,
                                    },
                                })
                            }
                            // fetch officeLocations for the selected company
                            if (nextValue?.id) {
                                getCompanyOffices({
                                    variables: { companyId: nextValue?.id },
                                })
                            }
                        }}
                    />

                    <FormControl
                        fullWidth
                        sx={{
                            marginBottom: getPxAsRem(24),

                            '&:has(.Mui-disabled)': {
                                opacity: 0.5,
                                cursor: 'not-allowed',
                            },
                        }}
                    >
                        <InputLabel id="office_label">
                            Company Office
                        </InputLabel>

                        <Select
                            id="company_office"
                            label="Company Office"
                            labelId="company_office_label"
                            disabled={
                                !entry.company ||
                                !(entry.company.choice in companyOffices) ||
                                isOfficeLocationDataLoading
                            }
                            onChange={(e: SelectChangeEvent<string>) => {
                                if (!entry.company) return

                                const companyOffice = companyOffices[
                                    entry.company.choice
                                ].find(
                                    (choiceBucket) =>
                                        choiceBucket.choice === e.target.value
                                )

                                updateEntry(entry.id, {
                                    companyOffice,
                                })
                            }}
                        >
                            {entry.company?.choice &&
                                entry.company.choice in companyOffices &&
                                companyOffices[entry.company.choice].map(
                                    (choiceBucket, index) => (
                                        <MenuItem
                                            key={`${choiceBucket.choice}_${index}`}
                                            value={choiceBucket.choice}
                                        >
                                            {choiceBucket.choice}
                                        </MenuItem>
                                    )
                                )}
                        </Select>
                    </FormControl>

                    <FormControl
                        fullWidth
                        style={{ marginBottom: getPxAsRem(24) }}
                    >
                        <InputLabel id="work_title">Work Title</InputLabel>
                        <Select
                            value={entry.workTitle}
                            id="title"
                            labelId="title_label"
                            label="Work Title"
                            onChange={(e) => {
                                const workTitle =
                                    workTitles.find(
                                        (title: string) =>
                                            title === e.target.value
                                    ) || ''
                                updateEntry(entry.id, { workTitle })
                            }}
                        >
                            {workTitles.map((title: string) => (
                                <MenuItem key={title} value={title}>
                                    {title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FieldWrapper>
                        <FormControl
                            fullWidth
                            style={{
                                marginRight: getPxAsRem(16),

                                width: '48%',
                            }}
                        >
                            <DatePicker
                                value={
                                    entry.startDate !== ''
                                        ? new Date(
                                              MMyyyyToValidDateString(
                                                  entry.startDate
                                              )
                                          )
                                        : null
                                }
                                format="MM/yyyy"
                                views={['year', 'month']}
                                onChange={(value: Date | null) => {
                                    const startDate = value
                                        ? formatDate(value, 'MM-yyyy')
                                        : ''
                                    updateEntry(entry.id, { startDate })
                                }}
                                label="Start Date"
                                maxDate={new Date()}
                            />
                        </FormControl>
                        <FormControl
                            fullWidth
                            style={{
                                width: '50%',
                            }}
                        >
                            <DatePicker
                                value={
                                    entry.endDate !== ''
                                        ? new Date(
                                              MMyyyyToValidDateString(
                                                  entry.endDate
                                              )
                                          )
                                        : null
                                }
                                format="MM/yyyy"
                                views={['year', 'month']}
                                onChange={(value: Date | null) => {
                                    const endDate = value
                                        ? formatDate(value, 'MM-yyyy')
                                        : ''
                                    updateEntry(entry.id, { endDate })
                                }}
                                label="End Date"
                                maxDate={new Date()}
                            />
                        </FormControl>
                    </FieldWrapper>

                    <FieldWrapper>
                        <FormControl
                            fullWidth
                            style={{
                                marginRight: getPxAsRem(16),
                                width: '48%',
                            }}
                        >
                            <InputLabel id="industry_label">
                                Industry
                            </InputLabel>
                            <Select
                                id="industry"
                                label="Industry"
                                labelId="industry_select_label"
                                placeholder="Select an option:"
                                onChange={(e: SelectChangeEvent<string>) => {
                                    if (e.target.value !== null) {
                                        const industry = industryChoices.find(
                                            (choice: ChoiceBucket) =>
                                                choice.choice === e.target.value
                                        )
                                        updateEntry(entry.id, { industry })
                                    }
                                }}
                            >
                                {industryChoices.map(
                                    (choicebucket: ChoiceBucket) => (
                                        <MenuItem
                                            key={choicebucket.choice}
                                            value={choicebucket.choice}
                                        >
                                            {choicebucket.choice}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth style={{ width: '50%' }}>
                            <InputLabel id="sector_label">Sector</InputLabel>
                            <Select
                                id="sector"
                                label="Sector"
                                labelId="sector_label"
                                placeholder="Select an option:"
                                onChange={(e: SelectChangeEvent<string>) => {
                                    if (e.target.value !== null) {
                                        const sector = sectorChoices.find(
                                            (choice: ChoiceBucket) =>
                                                choice.choice === e.target.value
                                        )
                                        updateEntry(entry.id, { sector })
                                    }
                                }}
                            >
                                {sectorChoices.map(
                                    (choicebucket: ChoiceBucket) => (
                                        <MenuItem
                                            key={choicebucket.choice}
                                            value={choicebucket.choice}
                                        >
                                            {choicebucket.choice}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </FieldWrapper>
                </Container>
            ))}
            <AdditionalButtonsContainer>
                {entries.length > 1 && (
                    <TertiaryButton
                        disabled={entries.length <= 1}
                        onClickHandler={removeEntry}
                        title={'Remove experience'}
                        style={{ marginRight: getPxAsRem(16) }}
                    />
                )}
                <SecondaryButton
                    title="Add another experience"
                    disabled={false}
                    onClickHandler={addEntry}
                />
            </AdditionalButtonsContainer>

            <NextButton
                disabled={!entries.every((entry) => entryIsValid(entry))}
                onClickHandler={submitData}
            />
        </IntakeCard>
    )
}

export default PriorEmploymentQuestion
